import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/roundnet-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "GM", title: "Best-of-three games", desc: "Record roundnet matches game by game, first to 21 with a two-point margin."},
    {code: "TM", title: "Pairs & teams", desc: "Keep two-player teams together across a season, or rotate partners."},
    {code: "WK", title: "Weekly rounds", desc: "Schedule recurring nets and match times across your venue or park."},
    {code: "LD", title: "Ladder play", desc: "Let teams challenge those above them and climb the rankings."},
    {code: "DV", title: "Skill divisions", desc: "Separate beginner, intermediate and advanced teams into their own groups."},
    {code: "TB", title: "Standings", desc: "Publish tables with wins, losses and point difference as results land."},
];

const LEAGUE_TYPES = [
    {name: "Round Robin Leagues", sub: "Everyone plays everyone", to: "/round-robin-tournament-generator/"},
    {name: "Ladder Leagues", sub: "Challenges and ranking movement", to: "/ladder-league-generator/"},
    {name: "Roundnet Tournaments", sub: "Pools and knockout brackets", to: "/roundnet-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Keep weekly play organised", desc: "Teams know which net they are on and who they are playing."},
    {title: "Scores kept in full", desc: "A 2-1 match keeps its game scores rather than becoming a single number."},
    {title: "Standings everyone can see", desc: "Rankings update from submitted results, on a page anyone can open."},
];

const USE_CASES = [
    {title: "For Roundnet Clubs", desc: "Run weekly league nights across nets and divisions."},
    {title: "For City Communities", desc: "Give a local scene a proper season instead of ad-hoc meetups."},
    {title: "For University Clubs", desc: "Run campus leagues across a term with clear standings."},
    {title: "For Beach & Park Play", desc: "Organise recurring outdoor rounds across multiple nets."},
    {title: "For Skill Divisions", desc: "Keep new players and advanced teams in separate groups."},
    {title: "For Ladder Competitions", desc: "Support challenges and ranking movement between teams."},
];

const COMPARE = [
    {need: "Weekly rounds", manual: "Rebuilt in a group chat each week", scorepal: "Scheduled rounds across nets and times"},
    {need: "Game scores", manual: "Only the winner noted", scorepal: "Every game recorded, best of three"},
    {need: "Ladder movement", manual: "Manual ranking edits", scorepal: "Rankings move automatically with results"},
    {need: "Divisions", manual: "Separate lists and duplicated tables", scorepal: "Each division with its own standings"},
    {need: "Player experience", manual: "Players ask who they play next", scorepal: "Fixtures and standings on a public page"},
];

const FAQS = [
    {
        q: 'What is roundnet league software?',
        a: <>Roundnet league software helps clubs and communities run recurring competition — scheduling weekly rounds, recording game scores, tracking team rankings and publishing standings across a season.</>,
    },
    {
        q: 'Can results be recorded game by game?',
        a: <>Yes. Roundnet matches are stored as a best of three, so a 21-18 19-21 21-15 result is kept in full rather than reduced to a single score.</>,
    },
    {
        q: 'Does ScorePal support roundnet ladders?',
        a: <>Yes. ScorePal supports <Link to={"/ladder-league-generator/" as any} className="text-[#082F8A] underline font-semibold">ladder-style</Link> roundnet leagues where teams challenge those above them and move up or down the rankings.</>,
    },
    {
        q: 'Can we run skill divisions?',
        a: <>Yes. Beginner, intermediate and advanced divisions each run as their own competition with their own fixtures and table.</>,
    },
    {
        q: 'Is this different from roundnet tournament software?',
        a: <>Yes. Roundnet league software is built for recurring weekly play across a season. <Link to={"/roundnet-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Roundnet tournament software</Link> is built for one-day events with pools and knockout brackets.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Roundnet League Software for Clubs & Communities | ScorePal</title>
            <meta name="description" content="Run roundnet (spikeball) leagues with ScorePal. Manage weekly rounds, pairs, best-of-three game scores, ladders, skill divisions and live standings from one platform." />
            <link rel="canonical" href={`${config.publicUrl}/roundnet-league-software/`} />
            <meta property="og:title" content="Roundnet League Software | ScorePal" />
            <meta property="og:description" content="Create roundnet league rounds, record game scores and publish standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/roundnet-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/roundnet-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Roundnet League Software | ScorePal" />
            <meta name="twitter:description" content="Run roundnet leagues, ladders, rankings and standings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Roundnet League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Roundnet league software for managing weekly rounds, pairs, game scores, ladders, divisions and standings.",
                "url": "https://scorepal.co/roundnet-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is roundnet league software?", "acceptedAnswer": {"@type": "Answer", "text": "Roundnet league software helps clubs and communities run recurring competition, scheduling weekly rounds, recording game scores, tracking team rankings and publishing standings across a season."}},
                    {"@type": "Question", "name": "Can results be recorded game by game?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Roundnet matches are stored as a best of three, so a 21-18 19-21 21-15 result is kept in full rather than reduced to a single score."}},
                    {"@type": "Question", "name": "Does ScorePal support roundnet ladders?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal supports ladder-style roundnet leagues where teams challenge those above them and move up or down the rankings."}},
                    {"@type": "Question", "name": "Can we run skill divisions?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Beginner, intermediate and advanced divisions each run as their own competition with their own fixtures and table."}},
                    {"@type": "Question", "name": "Is this different from roundnet tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Roundnet league software is built for recurring weekly play across a season, while roundnet tournament software is built for one-day events with pools and knockout brackets."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Roundnet League Software", item: "https://scorepal.co/roundnet-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Roundnet League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Give your roundnet community a real season, not just meetups
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps roundnet clubs run weekly leagues and ladders — with best-of-three game
                                    scores, pairs kept together across the season, skill divisions and standings every
                                    team can follow.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/roundnet-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live standings card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live roundnet standings</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Teams see where they sit as each week's results come in.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    <div className="grid grid-cols-[28px_1fr_48px_44px] items-center gap-2 pb-2 border-b border-white/15 font-inter text-white/50 text-xs uppercase tracking-wide">
                                        <span>#</span><span>Team</span><span className="text-center">W-L</span><span className="text-right">Pts</span>
                                    </div>
                                    {[
                                        {pos: 1, team: "Net Gains", rec: "8-1", pts: "24"},
                                        {pos: 2, team: "Rally Cats", rec: "6-3", pts: "19"},
                                        {pos: 3, team: "Spike Squad", rec: "4-5", pts: "13"},
                                        {pos: 4, team: "Bounce House", rec: "1-8", pts: "5"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[28px_1fr_48px_44px] items-center gap-2 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.team}</strong>
                                            <span className="font-inter text-white/70 text-sm text-center">{row.rec}</span>
                                            <strong className="font-inter text-white text-sm text-right">{row.pts}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to run a roundnet league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace the group chat fixtures, the scores nobody wrote down and the rankings held in someone's head.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of roundnet league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        A weekly round-robin season, a rolling ladder, or a one-day tournament — ScorePal handles the structure.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Weekly play + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Designed for weekly roundnet play
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Most roundnet scenes start as a group chat and a few nets in a park. A league gives that
                            structure — teams that stay together, results that count, and a table worth chasing.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Summer League — Round 5", r: "Active", badge: "live"},
                                {l: "Next match", r: "Net Gains vs Spike Squad"},
                                {l: "Net", r: "Net 2 · Saturday 11:00"},
                                {l: "Last result", r: "21-18 19-21 21-15"},
                                {l: "Top of the table", r: "Net Gains", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs group chats for roundnet leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        A chat thread is fine for organising a session, but it forgets every score the moment the next message lands.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Group chats</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for clubs, campuses and city communities
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        From a park meetup that outgrew its group chat to a campus league across a term, ScorePal keeps roundnet organised.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a one-day roundnet tournament?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is about recurring leagues. If you are running pool play into knockout brackets
                            across a single day, see our roundnet tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/roundnet-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Pool play</li>
                            <li>Knockout brackets</li>
                            <li>Skill divisions</li>
                            <li>Net scheduling</li>
                            <li>Finals scheduling</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next roundnet league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Weekly rounds, best-of-three game scores, ladders, skill divisions and live standings — roundnet league software built for growing communities.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Weekly Rounds", "Game Scores", "Ladder Play", "Pairs & Teams", "Divisions", "Live Standings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Roundnet League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
